import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/joy";
import { useTranslation } from "react-i18next";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { ProgramContentType } from "data/BlockComponent";
import {
  applyAutoFill,
  getFilledCells,
  isReplyOnlyRequest,
  isSameStructure,
  toCellContent,
  withFeedbackSent,
} from "logic/autoFill";
import { buildUserSimRequest } from "logic/userSimRequest";
import { getTaskIdFromTaskKey, isAllUserFieldFilled } from "logic/logics";
import useAutoFillStream from "hooks/useAutoFillStream";
import { getLatestTranslationVersion } from "hooks/useGetBlankTaskData";
import { userAtom } from "recoil/userAtom";
import { patientAtom } from "recoil/patientAtom";
import { taskDataAtom } from "recoil/taskDataAtom";
import { autoFilledCellAtom } from "recoil/autoFilledCellAtom";
import CustomModal from "components/common/CustomModal";
import AutoFillProgress from "./AutoFillProgress";

// 교육용 모의 내담자 계정 전용 — user_sim Agent 로 활동지를 마치기 직전 상태까지 채운다.
export default function AutoFillButton({
  taskKey,
  thoughtRecordKey,
  title,
  translationVersion,
  contentData,
  setContentData,
  setMoveToIndex,
}: {
  taskKey: string;
  thoughtRecordKey?: string;
  title: string;
  translationVersion?: string;
  contentData: ProgramContentType[];
  setContentData: React.Dispatch<
    React.SetStateAction<ProgramContentType[] | undefined>
  >;
  setMoveToIndex?: React.Dispatch<React.SetStateAction<number | undefined>>;
}) {
  const {
    phase,
    request,
    steps,
    currentKey,
    values,
    error,
    start,
    cancel,
    reset,
  } = useAutoFillStream();

  // Cell.tsx 와 같은 번역 범위
  const { t } = useTranslation("translation", {
    keyPrefix: `task.activity.${getTaskIdFromTaskKey(taskKey)}.${
      translationVersion ??
      getLatestTranslationVersion(getTaskIdFromTaskKey(taskKey))
    }`,
  });
  const { t: t_common } = useTranslation("translation", {
    keyPrefix: "task.activity.common",
  });
  const user = useRecoilValue(userAtom);
  const patient = useRecoilValue(patientAtom);
  const [taskDataState, setTaskDataState] = useRecoilState(taskDataAtom);
  const setAutoFilledCell = useSetRecoilState(autoFilledCellAtom);

  const fieldMap = useMemo(
    () => new Map((request?.fields || []).map((field) => [field.key, field])),
    [request]
  );
  // 요청을 만든 시점의 내용 — 칸 키가 이 배치 기준이라, 그사이 구조가 바뀌면(코멘트 추가 등) 적용하지 않는다
  const [baseContent, setBaseContent] = useState<ProgramContentType[]>();
  const isStructureChanged =
    !!baseContent && !isSameStructure(baseContent, contentData);
  const result = useMemo(
    () =>
      phase === "done" && request && !isStructureChanged
        ? applyAutoFill(contentData, request.fields, values)
        : undefined,
    [phase, request, isStructureChanged, contentData, values]
  );
  // 답할 코멘트가 있으면 답변 칸만 채우므로(마치기 전일 수도 있다) 마치기 조건 대신 답변 칸이 다 찼는지 본다
  const canApply =
    !!result &&
    (isReplyOnlyRequest(request?.fields || [])
      ? (request?.fields || []).every(
          (field) =>
            field.fill === "none" || !!toCellContent(field, values[field.key])
        )
      : isAllUserFieldFilled(withFeedbackSent(result), false));
  const invalidCount = Object.entries(values).filter(([key, value]) => {
    const field = fieldMap.get(key);
    return field && field.fill !== "none" && !toCellContent(field, value);
  }).length;

  const handleStart = () => {
    setBaseContent(contentData);
    start(() =>
      buildUserSimRequest({
        taskKey,
        title,
        thoughtRecordKey,
        content: contentData,
        t,
        t_common,
        accessToken: user?.accessToken || "",
        pid: patient?.patientId,
        taskDataState,
        setTaskDataState,
      })
    );
  };

  const handleApply = () => {
    if (!result) return;
    (document.activeElement as HTMLElement)?.blur();
    const filled = getFilledCells(contentData, result);
    setContentData(result);
    setAutoFilledCell(filled);
    // 처음 채운 블록으로 이동
    const firstBlockIndex = Math.min(
      ...Object.keys(filled).map((key) => Number(key.split("-")[0]))
    );
    if (Number.isFinite(firstBlockIndex)) setMoveToIndex?.(firstBlockIndex);
    reset();
  };

  // 진행 패널은 진행 표시·잠금용 — 다 쓰면 바로 채우고 닫는다. 채울 수 없을 때만 확인 모달로 이유를 보여준다
  useEffect(() => {
    if (phase === "done" && canApply) handleApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, canApply]);

  return (
    <>
      <Button
        size="sm"
        variant="Outlined"
        disabled={phase !== "idle"}
        onClick={handleStart}
        sx={{ whiteSpace: "nowrap", flexShrink: 0 }}
      >
        자동 채우기
      </Button>
      <AutoFillProgress
        open={phase === "running" || phase === "canceling"}
        isStopping={phase === "canceling"}
        onStop={cancel}
        steps={steps}
        fields={request?.fields || []}
        values={values}
        currentKey={currentKey}
      />
      {/* 확인이 필요한 경우(실패·적용 불가)만 확인 모달 */}
      <CustomModal
        open={phase === "error" || (phase === "done" && !canApply)}
        onClose={() => {}}
        title={"자동 채우기를 하지 못했어요"}
        subtitle={
          phase === "error"
            ? error
            : isStructureChanged
              ? "작성 중에 활동지 내용이 바뀌었어요. 다시 시도해 주세요"
              : invalidCount > 0
                ? `형식이 맞지 않는 값이 ${invalidCount}개 있어 적용할 수 없어요`
                : "채우지 못한 칸이 있어 적용할 수 없어요"
        }
        actions={[{ label: "확인", onClick: reset, variant: "Solid" }]}
      />
    </>
  );
}
